/**
 * Session/auth context (Phase 5b) — the SPA's single source of truth
 * for "who is the browser currently authenticated as", replacing the
 * old `AdminGate` localStorage-flag/shared-password model.
 *
 * On mount: `GET /api/auth/whoami`. That call NEVER errors/401s (see
 * `app/api/auth.py::whoami` / `services/auth/principal.py::
 * resolve_principal`) — it always resolves to SOME `Principal`, with
 * `kind: 'anonymous'` when no session cookie is present or it doesn't
 * resolve. So `principal` is `null` ONLY while the initial fetch is
 * still in flight (`loading`); once loaded it's always a real
 * `Principal` object. Consumers gate UI on `principal?.kind ===
 * 'user'` (or `principal.is_superadmin`), not on `principal` being
 * non-null — see `RequireSession` (App.tsx) for the actual route gate.
 *
 * Provider scope: wraps the WHOLE app (mounted in `App.tsx`, outside
 * `<BrowserRouter>`), not just the operator routes — `LoginPage` and
 * `InviteAcceptPage` are public routes that still need to read/update
 * this context, and the initial whoami call is cheap + harmless for
 * anonymous visitors to the public viewer/join pages too.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import api from '@/services/api';
import { Principal } from '@/types';

const ANONYMOUS: Principal = {
  kind: 'anonymous',
  user_id: null,
  device_id: null,
  is_superadmin: false,
  mfa_state: null,
};

interface AuthSessionCtx {
  /** `null` only while the initial whoami fetch is in flight;
   *  otherwise always a real `Principal` (`kind: 'anonymous'` when
   *  logged out). */
  principal: Principal | null;
  /** True only for the very first whoami fetch on mount — NOT set
   *  again on `refresh()` calls, so a logout/login transition doesn't
   *  flash a loading state over already-rendered UI. */
  loading: boolean;
  /** Re-fetch whoami and update context — call after login/invite-
   *  accept/logout so the whole app sees the new identity immediately. */
  refresh: () => Promise<Principal>;
  /** POST /api/auth/logout, then refresh (collapses to anonymous). */
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthSessionCtx | null>(null);

export const AuthSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<Principal> => {
    try {
      const next = await api.whoami();
      setPrincipal(next);
      return next;
    } catch {
      // whoami itself shouldn't error (see module docstring), but
      // stay safe: treat any transport failure (offline, backend
      // down) as "not authenticated" rather than leaving the UI stuck
      // on a stale identity forever.
      setPrincipal(ANONYMOUS);
      return ANONYMOUS;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Deliberately once on mount only — refresh() is otherwise called
    // explicitly by whatever action changed the identity (login,
    // invite-accept, logout), not on a poll interval. A session
    // dying mid-use surfaces via the axios/fetch 401 interceptor's
    // redirect (services/csrf.ts), not by this context noticing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Always refresh, even if the POST itself failed (e.g. the
      // session was already gone server-side) — either way the
      // correct end state is "collapse to anonymous", and refresh()
      // resolves that from the server rather than assuming.
      await refresh();
    }
  }, [refresh]);

  const value = useMemo<AuthSessionCtx>(
    () => ({ principal, loading, refresh, logout }),
    [principal, loading, refresh, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useAuthSession(): AuthSessionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useAuthSession() must be called inside <AuthSessionProvider>');
  }
  return ctx;
}
